import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { readAuthoredItem, type AuthoredItem } from "../../../../api/content/authoring/processAuthoringApi";
import { useLoad } from "../../../../shared/loading/useLoad";
import { appPaths } from "../../../../shared/routing/appPaths";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import TermEditView from "../../../ontology/term/TermEditView";
import ItemEditForm from "../../../processAuthoring/ItemEditForm";
import { useAuthoringOptions } from "../../../processAuthoring/draft/authoringOptions";
import { DerivationRootContext } from "../../../processAuthoring/draft/itemEdits";
import { hasEditForm } from "../../../processAuthoring/itemEditForms";
import { useUserPreferences } from "../../../profile/preferences/userPreferences";
import WorkflowEditMode from "../../../workflows/edit/WorkflowEditMode";
import { useThreadProcess } from "../../data/useThreadProcess";
import type { EditedItem } from "../../prepare/author/editedItem";

interface EditedItemEditorProps {
  readonly edited: EditedItem;
  readonly projectId: string;
  /** The model version a term is edited at: the Thread's, or the one the preparation lists. */
  readonly contentCommit: string;
  /** For a Workflow: a step or sub-workflow version to add to its draft (Use in a Workflow). */
  readonly carry?: string | null;
}

/**
 * The "Edit an item" step's view (ADR-0031 D2), in its Thread or before it: today's form or
 * editor for what the step edits, at its exact version. It saves through the step around it
 * (`ChangeSetStepContext`); opening it writes nothing.
 */
export default function EditedItemEditor({ edited, projectId, contentCommit, carry = null }: EditedItemEditorProps) {
  if (edited.kind === "term") return <TermEditView projectId={projectId} commit={contentCommit} iri={edited.iri} />;
  return (
    <DerivationRootContext.Provider value={edited.within ?? null}>
      <ItemEditorOf reference={edited.reference} projectId={projectId} carry={carry} />
    </DerivationRootContext.Provider>
  );
}

function ItemEditorOf({ reference, projectId, carry }: { reference: string; projectId: string; carry: string | null }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readAuthoredItem(reference, signal), [reference]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const item = state.value;
  const kind = item.content?.kind;
  if (kind === "workflow") return <WorkflowEditorOf reference={item.reference} projectId={projectId} carry={carry} />;
  if (!hasEditForm(kind)) {
    return (
      <p className="text-sm">
        {t("authoring.edit.withAgent")}{" "}
        <Link to={appPaths.item(item.reference)} className="app-link app-link-cue-away underline">{t("authoring.edit.openItem")}</Link>
      </p>
    );
  }
  return <FormWithOptions item={item} projectId={projectId} />;
}

function FormWithOptions({ item, projectId }: { item: AuthoredItem; projectId: string }) {
  const { t } = useTranslation();
  const { state, reload } = useAuthoringOptions(projectId);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("authoring.loadingOptions")} onRetry={reload} />;
  return <ItemEditForm key={item.reference} item={item} projectId={projectId} options={state.value} />;
}

/** A Workflow is edited in its graph editor, here in the step (AU-6). */
function WorkflowEditorOf({ reference, projectId, carry }: { reference: string; projectId: string; carry: string | null }) {
  const { t } = useTranslation();
  const process = useThreadProcess(reference, projectId);
  const density = useUserPreferences().preferences.workflowGraphDensity;
  if (process.state.status !== "loaded") return <LoadStatus state={process.state} loadingLabel={t("workflows.edit.loading")} onRetry={process.reload} />;
  if (process.state.value === null) return null;
  // Leaving the editor leaves the step waiting; the step's own controls stay above.
  // The step has no density control of its own: its graph shows the user's default (F76).
  return <WorkflowEditMode projectId={projectId} reference={reference} process={process.state.value} density={density} carry={carry} />;
}
